import React, { useEffect, useState } from 'react';
import { X, Loader2, FileText } from 'lucide-react';
import { useStorageUrl } from '../chatHome/MemberAvatar';
import { SpecialistIcon } from '../chatHome/specialistIcons';
import { getConversationMessages, type ConversationMessage, type MessageAttachment } from '../../services/conversationService';

/** One attachment in the read-only stream — resolves its storage URL and renders the right player. */
const AttachmentView: React.FC<{ att: MessageAttachment }> = ({ att }) => {
  const url = useStorageUrl(att.storage_path || undefined);
  if (att.kind === 'image') return url ? <img src={url} alt={att.name} loading="lazy" className="mt-2 max-h-60 rounded-lg border border-secondary-200 dark:border-secondary-700" /> : null;
  if (att.kind === 'video') return url ? <video src={url} controls playsInline className="mt-2 max-h-60 w-full rounded-lg border border-secondary-200 dark:border-secondary-700" /> : null;
  if (att.kind === 'audio') return url ? <audio src={url} controls className="mt-2 w-full" /> : null;
  return (
    <span className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-secondary-200 dark:border-secondary-700 px-2 py-1 text-xs text-secondary-500">
      <FileText className="h-3.5 w-3.5" /> {att.name}
    </span>
  );
};

/**
 * Read-only viewer for an ARCHIVED chat — opens the message stream in a modal (no composer, no
 * actions) so the owner can revisit a chat without unarchiving it or leaving Account settings.
 */
const ArchivedChatViewer: React.FC<{ conversationId: string; title: string; onClose: () => void }> = ({ conversationId, title, onClose }) => {
  const [messages, setMessages] = useState<ConversationMessage[] | null>(null);
  useEffect(() => {
    let alive = true;
    getConversationMessages(conversationId).then((m) => { if (alive) setMessages(m); }).catch(() => { if (alive) setMessages([]); });
    return () => { alive = false; };
  }, [conversationId]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="flex w-full max-w-2xl max-h-[88dvh] flex-col rounded-2xl bg-white dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-secondary-200 dark:border-secondary-800">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-secondary-900 dark:text-white">{title}</h2>
            <p className="text-[11px] text-secondary-400">Archived chat · read-only</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-secondary-400 hover:text-secondary-700 dark:hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {messages === null ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-secondary-400" /></div>
          ) : messages.length === 0 ? (
            <p className="py-10 text-center text-sm text-secondary-400">No messages in this chat.</p>
          ) : (
            messages.map((m) => (
              <div key={m.id} className="flex gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-100 dark:bg-secondary-800">
                  <SpecialistIcon name={m.avatar_icon || 'user'} className="h-4 w-4 text-secondary-600 dark:text-secondary-300" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-secondary-500">{m.author_name}</p>
                  {m.content && <p className="whitespace-pre-wrap break-words text-sm text-secondary-800 dark:text-secondary-100">{m.content}</p>}
                  {(m.attachments || []).map((att, i) => <AttachmentView key={i} att={att} />)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ArchivedChatViewer;
